'use client';

import React from 'react';
import { FacebookIcon, InstagramIcon, YoutubeIcon, MailIcon, PhoneIcon, MapPinIcon } from 'lucide-react';
import WhatsAppIcon from '@/components/icons/WhatsAppIcon';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import { useTenantContext } from '@/contexts/TenantContext';

export default function TenantFooter({ subdomain }: { subdomain: string }) {
    const { toast } = useToast();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const { tenant } = useTenantContext();

    async function handleNewsletterSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
            toast({
                title: 'E-mail inválido',
                description: 'Por favor, insira um e-mail válido.',
                variant: 'destructive',
            });
            return;
        }
        setLoading(true);
        try {
            const response = await fetch('/api/v1/newsletter', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 409) {
                    toast({
                        title: 'E-mail já cadastrado',
                        description: 'Este e-mail já está inscrito na newsletter.',
                        variant: 'default',
                    });
                } else {
                    toast({
                        title: 'Erro ao cadastrar',
                        description: data.error || 'Não foi possível cadastrar seu e-mail. Tente novamente.',
                        variant: 'destructive',
                    });
                }
                return;
            }

            setEmail('');
            toast({
                title: 'Inscrição realizada!',
                description: 'Você receberá novidades em breve.',
                variant: 'default',
            });
        } catch (err) {
            toast({
                title: 'Erro ao cadastrar',
                description: 'Não foi possível cadastrar seu e-mail. Tente novamente.',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    }

    // ✅ CORRIGIDO: Usar configurações aninhadas do tenant
    const config = tenant?.configurations || {};

    // Aplicar cores personalizadas do tenant
    const footerStyle = {
        backgroundColor: config.cor_fundo_footer || '#2C2C2C' // ✅ CORRIGIDO: Usar cor de fundo do footer
    };

    const accentStyle = {
        color: config.cor_destaque || '#C5A572' // ✅ RESTAURADO: Usar cor de destaque (dourado)
    };

    const primaryStyle = {
        color: config.cor_primaria || '#1B365D'
    };

    // Domínio personalizado: links relativos (middleware já faz o rewrite).
    // Subdomínio sistema: prefixo /tenant/${subdomain} necessário.
    const linkBase = tenant?.domain ? '' : `/tenant/${subdomain}`;

    const rawPhone = tenant?.telefone?.replace(/\D/g, '') ?? '';
    const whatsappHref = rawPhone
        ? `https://wa.me/${rawPhone.startsWith('55') ? rawPhone : `55${rawPhone}`}`
        : '';

    return (
        <footer className="text-[#F8F5F0] border-t-0" style={footerStyle}>
            <div className="container mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-4 gap-8">
                <div>
                    <span className="text-2xl font-bold" style={accentStyle}>
                        {tenant?.nome || 'Wesley Reis'}
                    </span>
                    <p className="mt-3 text-sm text-[#F8F5F0]">
                        {tenant?.bio || 'Especialista em imóveis de alto padrão, oferecendo experiências únicas para quem busca exclusividade e sofisticação.'}
                    </p>
                    <div className="flex gap-3 mt-4">
                        {tenant?.telefone && (
                            <a href={whatsappHref}
                                aria-label="WhatsApp"
                                className="hover:opacity-70"
                                target="_blank"
                                rel="noopener noreferrer">
                                <WhatsAppIcon className="w-5 h-5" style={{ color: config.cor_destaque || '#C5A572' }} />
                            </a>
                        )}
                        {tenant?.facebook_url && (
                            <a href={tenant.facebook_url} aria-label="Facebook" className="hover:opacity-70" target="_blank" rel="noopener noreferrer">
                                <FacebookIcon className="w-5 h-5" style={{ color: config.cor_destaque || '#C5A572' }} />
                            </a>
                        )}
                        {tenant?.instagram_url && (
                            <a href={tenant.instagram_url} aria-label="Instagram" className="hover:opacity-70" target="_blank" rel="noopener noreferrer">
                                <InstagramIcon className="w-5 h-5" style={{ color: config.cor_destaque || '#C5A572' }} />
                            </a>
                        )}
                        {tenant?.youtube_url && (
                            <a href={tenant.youtube_url} aria-label="YouTube" className="hover:opacity-70" target="_blank" rel="noopener noreferrer">
                                <YoutubeIcon className="w-5 h-5" style={{ color: config.cor_destaque || '#C5A572' }} />
                            </a>
                        )}
                    </div>
                </div>

                <div>
                    <span className="text-lg font-bold" style={accentStyle}>Links Rápidos</span>
                    <ul className="mt-3 space-y-2 text-[#F8F5F0]">
                        <li><a href={`${linkBase}/`} className="hover:text-[#C5A572]" aria-label="Página Inicial">Página Inicial</a></li>
                        <li><a href={`${linkBase}/imoveis`} className="hover:text-[#C5A572]" aria-label="Imóveis">Imóveis</a></li>
                        <li><a href={`${linkBase}/contato`} className="hover:text-[#C5A572]">Contato</a></li>
                    </ul>
                </div>

                <div>
                    <span className="text-lg font-bold" style={accentStyle}>Contato</span>
                    <ul className="mt-3 space-y-2 text-[#F8F5F0]">
                        <li>
                            <PhoneIcon className="inline w-4 h-4 mr-1" style={{ color: config.cor_destaque || '#C5A572' }} />
                            {tenant?.telefone || '(62) 998232-9009'}
                        </li>
                        <li>
                            <MailIcon className="inline w-4 h-4 mr-1" style={{ color: config.cor_destaque || '#C5A572' }} />
                            {tenant?.email || 'contato@porttal360.com.br'}
                        </li>
                        <li>
                            <MapPinIcon className="inline w-4 h-4 mr-1" style={{ color: config.cor_destaque || '#C5A572' }} />
                            {tenant?.endereco_rua && tenant?.endereco_numero && tenant?.endereco_cidade && tenant?.endereco_estado
                                ? `${tenant.endereco_rua}, ${tenant.endereco_numero} - ${tenant.endereco_cidade}, ${tenant.endereco_estado}`
                                : 'Av. T-9, 2310 - Goiânia, GO'
                            }
                        </li>
                    </ul>
                </div>

                <div>
                    <span className="text-lg font-bold" style={accentStyle}>Newsletter</span>
                    <p className="mt-3 text-sm">Receba informações exclusivas sobre lançamentos de luxo.</p>
                    <form className="flex flex-wrap mt-3 max-w-full" onSubmit={handleNewsletterSubmit}>
                        <input
                            type="email"
                            placeholder="Seu email"
                            className="flex-1 min-w-0 px-3 py-2 rounded-l-lg text-[#F8F5F0] border-0 focus:outline-none max-w-[220px] md:max-w-xs"
                            style={{ backgroundColor: config.cor_nome_primaria || '#1B365D' }} // ✅ CORRIGIDO: Usar cor nome primária
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            disabled={loading}
                        />
                        <button
                            type="submit"
                            className="px-4 rounded-r-lg font-bold"
                            style={{ backgroundColor: config.cor_destaque || '#C5A572', color: config.cor_primaria || '#1B365D' }} // ✅ RESTAURADO: Usar cor de destaque (dourado)
                            disabled={loading}
                            aria-label="Inscrever-se na newsletter"
                        >
                            <MailIcon className="w-5 h-5" style={{ color: config.cor_primaria || '#1B365D' }} />
                        </button>
                    </form>
                </div>
            </div>

            <div className="border-t border-gray-700 mt-12 pt-8 flex flex-col items-center justify-center text-center">
                <p className="text-gray-400 text-sm mb-2">
                    © {new Date().getFullYear()} {tenant?.nome || 'Wesley Reis'}. Todos os direitos reservados.
                </p>
                <div className="flex space-x-6">
                    <a
                        href={`${linkBase}/politica-de-privacidade`}
                        className="text-gray-400 hover:text-[#C5A572] text-sm transition-colors duration-300"
                    >
                        Política de Privacidade
                    </a>
                    <a
                        href={`${linkBase}/termos-de-uso`}
                        className="text-gray-400 hover:text-[#C5A572] text-sm transition-colors duration-300"
                    >
                        Termos de Uso
                    </a>
                </div>
            </div>
        </footer>
    );
}
